// FM-15 — internal arithmetic inconsistency between summary and detail tables.
//
// This is the check that found a 445,144 kWh break in DOE/FEMP's own model M&V
// plan. It is also the check most easily got wrong: reconstructing tables from a
// PDF text layer is unreliable, and a misread cell would produce a confident
// wrong finding — the worst possible output for a tool whose whole claim is
// traceability.
//
// So nothing is auto-selected. The engine proposes, the user confirms which
// table is the summary and which detail table belongs to each ECM, and only then
// does the arithmetic run. Everything after the confirmation is exact.
//
// Flag ids are derived from what the comparison was — the column, the summary
// row — and never from a running counter. The reviewer can answer one further
// question (which table gives the rate) after deciding on findings this check
// has already produced, which re-runs it; a counter would renumber the findings
// underneath the decisions they have already made.

import type { ExtractedTable, Flag, Receipt, SourceDocument } from '../../types';
import { evidenceFor, detectionSignal, tierOf } from '../../data/taxonomy';
import { parseNumericCell } from '../ingest/tableParse';
import { buildLocator, type Locator } from './locate';
import { enrichFlag } from '../finding';

export const CHECK_ID = 'table-reconciliation-check';

/* ==========================================================================
   Reading a parsed table
   ========================================================================== */

export interface TableShape {
  table: ExtractedTable;
  /** Leading rows carrying no figures: the (often multi-line) header block. */
  headerRows: string[][];
  /** Rows carrying at least one figure. */
  dataRows: { index: number; cells: string[]; label: string }[];
  /** Synthesised column names, from the header block plus the header row. */
  columnLabels: string[];
  /** Columns that actually carry figures. */
  dataColumns: number[];
  /** Columns whose label was taken from the header column to their left. */
  borrowedLabels: Record<number, number>;
}

/**
 * A figure is a cell that is *only* a number, give or take a currency symbol,
 * thousands separators, a percent sign or accounting parentheses.
 *
 * The looser test — does the cell contain a number — reads the header fragment
 * "1 ($/yr)" as the figure 1, which truncates the header block and loses the
 * "(kWh/yr)" row that names the column. That is how this check first failed to
 * find the EMCS disagreement at all.
 */
export function isFigureCell(raw: string): boolean {
  const text = raw.trim();
  if (!text) return false;
  if (!/^\(?\s*[$€£]?\s*-?[\d,]+(?:\.\d+)?\s*%?\s*\)?$/.test(text)) return false;
  return parseNumericCell(text) !== null;
}

function figureCount(cells: string[]): number {
  return cells.filter(isFigureCell).length;
}

/**
 * A numbered section heading that the table parser swept up, e.g.
 * "4.6  Post-Installation M&V Activities" sitting under Table 4-4.
 */
function isSectionHeadingRow(cells: string[]): boolean {
  const figures = cells.filter(isFigureCell);
  if (figures.length !== 1) return false;
  if (!/^\d+(\.\d+)*$/.test(figures[0].trim())) return false;
  return cells.some((c) => !isFigureCell(c) && c.trim().length >= 20);
}

/** The row's text label: its leading non-figure cells, joined. */
function labelOf(cells: string[]): string {
  const parts: string[] = [];
  for (const cell of cells) {
    if (isFigureCell(cell)) break;
    if (cell.trim()) parts.push(cell.trim());
  }
  return parts.join(' ').trim();
}

export function readTable(table: ExtractedTable): TableShape {
  const all = [table.headers, ...table.rows];

  // The data block opens at the first row carrying two or more figures. One
  // stray figure is more often a unit fragment or a section number.
  let firstDataAt = all.findIndex((r) => figureCount(r) >= 2 && !isSectionHeadingRow(r));
  if (firstDataAt < 0) {
    firstDataAt = all.findIndex((r) => figureCount(r) >= 1 && !isSectionHeadingRow(r));
  }

  const headerRows = firstDataAt <= 0 ? [table.headers] : all.slice(0, firstDataAt);
  const body = firstDataAt < 0 ? [] : all.slice(firstDataAt);

  const width = Math.max(...all.map((r) => r.length), 0);
  const columnLabels: string[] = [];
  for (let c = 0; c < width; c++) {
    const seen: string[] = [];
    for (const row of headerRows) {
      const cell = (row[c] ?? '').trim();
      if (cell && !seen.includes(cell)) seen.push(cell);
    }
    columnLabels.push(seen.join(' '));
  }

  // Rows are numbered as the source table numbers them: headers is row 0.
  const offset = firstDataAt < 0 ? 0 : firstDataAt;
  const dataRows = body
    .map((cells, i) => ({ index: offset + i, cells, label: labelOf(cells) }))
    .filter((r) => figureCount(r.cells) >= 1 && !isSectionHeadingRow(r.cells));

  const dataColumns: number[] = [];
  for (let c = 0; c < width; c++) {
    if (dataRows.some((r) => isFigureCell(r.cells[c] ?? ''))) dataColumns.push(c);
  }

  // A header that sits in the column immediately left of a figure column, in a
  // column carrying no figures of its own, belongs to that figure column: wide
  // header text starts to the left of right-aligned numbers. Only the immediate
  // left neighbour is borrowed from, and only when it holds no data itself, so a
  // label is never taken across another populated column.
  const borrowedLabels: Record<number, number> = {};
  for (const c of dataColumns) {
    if (columnLabels[c]) continue;
    const left = c - 1;
    if (left < 0) continue;
    if (dataColumns.includes(left)) continue;
    if (!columnLabels[left]) continue;
    columnLabels[c] = columnLabels[left];
    borrowedLabels[c] = left;
  }

  return { table, headerRows, dataRows, columnLabels, dataColumns, borrowedLabels };
}

/** Finds a column by unit, considering only columns that carry figures. */
export function findDataColumn(shape: TableShape, unit: RegExp): number {
  for (const c of shape.dataColumns) {
    if (unit.test(shape.columnLabels[c] ?? '')) return c;
  }
  return -1;
}

/** How a cell is cited in a receipt, so every figure can be found in the PDF. */
export function cellRef(
  shape: TableShape,
  row: number,
  col: number,
  rowLabel?: string,
): string {
  const column = shape.columnLabels[col]?.replace(/\s+/g, ' ').trim();
  // Where a column's heading is printed above the column to its left, say so,
  // so a reader looking for the heading in the PDF finds it.
  const borrowedFrom = shape.borrowedLabels?.[col];
  const columnNote =
    borrowedFrom === undefined
      ? ''
      : `, heading printed above column ${borrowedFrom}`;
  const parts = [
    shape.table.label,
    `page ${shape.table.page}`,
    `row ${row}${rowLabel ? ` (${rowLabel})` : ''}`,
    `column ${col}${column ? ` (${column}${columnNote})` : ''}`,
  ];
  return parts.join(', ');
}

/* ==========================================================================
   Step 1 — propose. Never select.
   ========================================================================== */

export interface Candidate {
  tableId: string;
  label: string;
  page: number;
  caption: string;
  score: number;
  reasons: string[];
}

const TOTAL_ROW = /^total/i;

function looksLikeEcmLabel(label: string): boolean {
  return label.length > 0 && !TOTAL_ROW.test(label);
}

export function proposeSummaryTables(tables: ExtractedTable[]): Candidate[] {
  const candidates: Candidate[] = [];

  for (const table of tables) {
    const shape = readTable(table);
    const reasons: string[] = [];
    let score = 0;

    const caption = table.caption.toLowerCase();
    if (/proposed annual savings (overview|summary)/.test(caption)) {
      score += 5;
      reasons.push('caption reads "Proposed Annual Savings Overview" or "Summary"');
    } else if (/savings summary|savings overview/.test(caption)) {
      score += 4;
      reasons.push('caption names a savings summary');
    } else if (/savings/.test(caption)) {
      score += 1;
      reasons.push('caption mentions savings');
    }

    const hasTotal = shape.dataRows.some((r) => TOTAL_ROW.test(r.label));
    if (hasTotal) {
      score += 3;
      reasons.push('has a Total row');
    }

    const ecmRows = shape.dataRows.filter((r) => looksLikeEcmLabel(r.label));
    if (ecmRows.length >= 2 && hasTotal) {
      score += 2;
      reasons.push(`${ecmRows.length} labelled rows above the total, one per apparent ECM`);
    }

    // A summary states project totals; a detail table states one measure.
    if (/for ecm|baseline/.test(caption)) {
      score -= 3;
      reasons.push('caption points at a single ECM, not the project');
    }

    if (score > 0) {
      candidates.push({
        tableId: table.id,
        label: table.label,
        page: table.page,
        caption: table.caption,
        score,
        reasons,
      });
    }
  }

  return candidates.sort((a, b) => b.score - a.score || a.page - b.page);
}

export function proposeDetailTables(tables: ExtractedTable[]): Candidate[] {
  const candidates: Candidate[] = [];

  for (const table of tables) {
    const shape = readTable(table);
    const reasons: string[] = [];
    let score = 0;

    if (/proposed annual savings for ecm/i.test(table.caption)) {
      score += 5;
      reasons.push('caption reads "Proposed Annual Savings for ECM"');
    }

    const labels = shape.dataRows.map((r) => r.label.toLowerCase());
    const hasBaseline = labels.some((l) => /baseline/.test(l));
    const hasSavings = labels.some((l) => /savings/.test(l));
    const hasPost = labels.some((l) => /post|installation/.test(l));

    if (hasBaseline) {
      score += 2;
      reasons.push('has a Baseline row');
    }
    if (hasPost) {
      score += 2;
      reasons.push('has a Post-installation row');
    }
    if (hasSavings) {
      score += 2;
      reasons.push('has a Savings row');
    }
    // The post-installation label is often mangled by PDF extraction; a
    // Baseline and a Savings row with a row between them is the same shape.
    if (hasBaseline && hasSavings && !hasPost && shape.dataRows.length >= 3) {
      score += 1;
      reasons.push('a row sits between Baseline and Savings, in the position of post-installation');
    }

    if (score > 0) {
      candidates.push({
        tableId: table.id,
        label: table.label,
        page: table.page,
        caption: table.caption,
        score,
        reasons,
      });
    }
  }

  return candidates.sort((a, b) => b.score - a.score || a.page - b.page);
}

/** A second project-level table that restates the same totals, e.g. Table 1-2. */
export function proposeOverviewTables(tables: ExtractedTable[]): Candidate[] {
  return tables
    .map((table) => {
      const reasons: string[] = [];
      let score = 0;
      if (/site use and savings overview|use and savings/i.test(table.caption)) {
        score += 4;
        reasons.push('caption names a site use and savings overview');
      } else if (/overview/i.test(table.caption)) {
        score += 2;
        reasons.push('caption names an overview');
      }
      return {
        tableId: table.id,
        label: table.label,
        page: table.page,
        caption: table.caption,
        score,
        reasons,
      };
    })
    .filter((c) => c.score > 0)
    .sort((a, b) => b.score - a.score);
}

export function proposeRateTables(tables: ExtractedTable[]): Candidate[] {
  return tables
    .map((table) => {
      const reasons: string[] = [];
      let score = 0;
      if (/rate schedule|rate data|energy rates?/i.test(table.caption)) {
        score += 4;
        reasons.push('caption names a rate schedule');
      }
      if (findUnitRate(table)) {
        score += 3;
        reasons.push('contains a per-unit rate below $1');
      }
      return {
        tableId: table.id,
        label: table.label,
        page: table.page,
        caption: table.caption,
        score,
        reasons,
      };
    })
    .filter((c) => c.score > 0)
    .sort((a, b) => b.score - a.score);
}

/**
 * The contract's own base energy rate. Rate cells often carry two figures —
 * "1.00  $0.0525" is an index and a rate in one cell — so the dollar token is
 * extracted directly rather than taken from the cell's leading number.
 */
export function findUnitRate(
  table: ExtractedTable,
): { value: number; raw: string; row: number; col: number } | null {
  const rows = [table.headers, ...table.rows];
  for (let r = 0; r < rows.length; r++) {
    for (let c = 0; c < rows[r].length; c++) {
      const cell = rows[r][c] ?? '';
      const match = /\$\s*(0?\.\d{3,5})\b/.exec(cell);
      if (match) {
        const value = Number(match[1]);
        if (Number.isFinite(value) && value > 0 && value < 1) {
          return { value, raw: `$${match[1]}`, row: r, col: c };
        }
      }
    }
  }
  return null;
}

/* ==========================================================================
   Step 2 — the user's confirmed selection
   ========================================================================== */

export interface EcmMatch {
  /** Row index in the summary table. */
  summaryRow: number;
  ecmLabel: string;
  /** null means the user declined to match this row. */
  detailTableId: string | null;
}

export interface ReconcileSelection {
  summaryTableId: string;
  ecmMatches: EcmMatch[];
  overviewTableId?: string;
  rateTableId?: string;
  /** tableId -> "row,col" -> corrected value, as typed by the user. */
  corrections?: Record<string, Record<string, string>>;
}

function corrected(
  selection: ReconcileSelection,
  tableId: string,
  row: number,
  col: number,
): string | undefined {
  return selection.corrections?.[tableId]?.[`${row},${col}`];
}

interface Value {
  value: number;
  raw: string;
  ref: string;
  userCorrected: boolean;
}

function valueAt(
  shape: TableShape,
  selection: ReconcileSelection,
  row: number,
  col: number,
  rowLabel?: string,
): Value | null {
  const all = [shape.table.headers, ...shape.table.rows];
  const override = corrected(selection, shape.table.id, row, col);
  const raw = override ?? all[row]?.[col] ?? '';
  const value = parseNumericCell(raw);
  if (value === null) return null;
  return {
    value,
    raw: raw.trim(),
    ref: cellRef(shape, row, col, rowLabel),
    userCorrected: override !== undefined,
  };
}

/* ==========================================================================
   Step 3 — reconcile. Exact arithmetic only.
   ========================================================================== */

export interface ReconcileOutcome {
  flags: Flag[];
  /**
   * Cells involved in a disagreement, as tableId -> ["row,col", ...], so the
   * confirmation tables can outline exactly the figures a finding rests on.
   */
  highlights: Record<string, string[]>;
  /** Checks that ran and agreed. A passed check is information. */
  clean: { title: string; detail: string; receipt: Receipt }[];
  /** Columns or rows that could not be checked, and why. */
  notChecked: { title: string; reason: string }[];
}

const MATCHES_WITHIN = 0.01; // 1%, for the rate check only

function fmt(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

// One locator per document. reconcile() emits a flag per disagreement and each
// needs the same page boundaries; rebuilding them per flag would be wasteful.
const locators = new WeakMap<SourceDocument, Locator>();

function locatorFor(doc: SourceDocument): Locator {
  const cached = locators.get(doc);
  if (cached) return cached;
  const built = buildLocator(doc);
  locators.set(doc, built);
  return built;
}

/** A verbatim line from the document containing this figure, for the quote. */
function quoteForFigure(doc: SourceDocument, raw: string): string | null {
  const needle = raw.trim();
  if (!needle) return null;
  const at = doc.rawText.indexOf(needle);
  if (at < 0) return null;
  const start = doc.rawText.lastIndexOf('\n', at) + 1;
  const end = doc.rawText.indexOf('\n', at);
  const line = doc.rawText.slice(start, end === -1 ? doc.rawText.length : end);
  return line.trim() || null;
}

/**
 * The plain-language claim, per comparison.
 *
 * The failure mode's own title — "Two tables report different savings for the
 * same measure" — is true of every finding this check makes, which means a
 * reviewer scanning the list sees the same sentence three times and cannot tell
 * the three apart. Each comparison knows exactly what it compared, so each says
 * it. No code, no tier, no column index: the table as the plan prints it, and
 * the measure as the plan names it.
 */
function makeFlag(
  doc: SourceDocument,
  id: string,
  claim: string,
  title: string,
  sectionRef: string,
  page: number | null,
  receipt: Receipt,
  quoteSeed: string | null,
): Flag {
  const quote = quoteSeed ? quoteForFigure(doc, quoteSeed) : null;
  return enrichFlag({
    id,
    failureModeId: 'FM-15',
    tier: tierOf('FM-15'),
    layer: 'deterministic',
    checkId: CHECK_ID,
    claim,
    title,
    quote,
    sectionRef,
    page,
    quoteOffsets: locatorFor(doc).offsetsOf(quote),
    basis: detectionSignal('FM-15', 0),
    evidenceCaseIds: evidenceFor('FM-15'),
    receipt,
    status: 'open',
  });
}

export function reconcile(
  doc: SourceDocument,
  selection: ReconcileSelection,
): ReconcileOutcome {
  const byId = new Map(doc.tables.map((t) => [t.id, t]));
  const summaryTable = byId.get(selection.summaryTableId);

  if (!summaryTable) {
    return {
      flags: [],
      highlights: {},
      clean: [],
      notChecked: [
        {
          title: 'Table reconciliation',
          reason: 'The confirmed summary table is no longer present in this document.',
        },
      ],
    };
  }

  const summary = readTable(summaryTable);
  const flags: Flag[] = [];
  const clean: ReconcileOutcome['clean'] = [];
  const notChecked: ReconcileOutcome['notChecked'] = [];
  const highlights: Record<string, string[]> = {};

  const mark = (tableId: string, row: number, col: number) => {
    const key = `${row},${col}`;
    const list = highlights[tableId] ?? (highlights[tableId] = []);
    if (!list.includes(key)) list.push(key);
  };

  const totalRow = summary.dataRows.find((r) => TOTAL_ROW.test(r.label));
  const ecmRows = summary.dataRows.filter(
    (r) => looksLikeEcmLabel(r.label) && r !== totalRow,
  );

  /* --- a) column sums ------------------------------------------------- */
  if (!totalRow) {
    notChecked.push({
      title: 'Column sums',
      reason: 'The confirmed summary table has no Total row to sum against.',
    });
  } else {
    const width = Math.max(
      ...[summary.table.headers, ...summary.table.rows].map((r) => r.length),
    );

    for (let col = 0; col < width; col++) {
      const total = valueAt(summary, selection, totalRow.index, col, totalRow.label);
      if (!total) continue;

      const parts = ecmRows.map((row) => ({
        row,
        value: valueAt(summary, selection, row.index, col, row.label),
      }));

      // Every contributing row must have a figure in this column. A column
      // where extraction left a hole cannot be summed — reporting a shortfall
      // there would be an artefact of the parse, not a finding in the plan.
      const missing = parts.filter((p) => p.value === null);
      if (missing.length > 0) {
        notChecked.push({
          title: `Column ${col}${summary.columnLabels[col] ? ` (${summary.columnLabels[col]})` : ''}`,
          reason: `${missing.length} of ${parts.length} rows have no figure in this column, so its total cannot be reconciled. Rows: ${missing
            .map((m) => m.row.label || `row ${m.row.index}`)
            .join(', ')}.`,
        });
        continue;
      }

      const values = parts.map((p) => p.value as Value);
      const sum = values.reduce((acc, v) => acc + v.value, 0);
      const delta = sum - total.value;

      const inputs = [
        ...values.map((v) => ({
          label: v.ref + (v.userCorrected ? ' — corrected by reviewer' : ''),
          value: v.raw,
        })),
        {
          label: total.ref + (total.userCorrected ? ' — corrected by reviewer' : ''),
          value: total.raw,
        },
      ];
      const computation = `${values.map((v) => fmt(v.value)).join(' + ')} = ${fmt(sum)}\nstated total = ${fmt(total.value)}\ndifference = ${fmt(sum)} - ${fmt(total.value)} = ${fmt(delta)}`;

      if (delta === 0) {
        clean.push({
          title: `Column sums: ${summary.columnLabels[col] || `column ${col}`}`,
          detail: `The rows sum exactly to the stated total, ${fmt(total.value)}.`,
          receipt: {
            kind: 'arithmetic',
            inputs,
            computation,
            result: `The rows sum to ${fmt(sum)} and the table states ${fmt(total.value)}. They agree exactly.`,
          },
        });
        continue;
      }

      const roundingNote =
        Math.abs(delta) <= values.length / 2
          ? ' The difference is small enough to be display rounding of the individual rows; check the underlying figures before treating it as a break.'
          : '';

      for (const part of parts) mark(summary.table.id, part.row.index, col);
      mark(summary.table.id, totalRow.index, col);

      flags.push(
        makeFlag(
          doc,
          `flag-${CHECK_ID}-sum-col${col}`,
          summary.columnLabels[col]?.trim()
            ? `${summary.table.label}: the ${summary.columnLabels[col].trim()} column doesn’t add up to the total it states`
            : `${summary.table.label}: the rows don’t add up to the total it states`,
          `${summary.table.label}: the rows do not sum to the stated total`,
          summary.table.label,
          summary.table.page,
          {
            kind: 'arithmetic',
            inputs,
            computation,
            result: `The ${values.length} rows sum to ${fmt(sum)}, but the table states a total of ${fmt(total.value)} — a difference of ${fmt(delta)}.${roundingNote}`,
          },
          total.raw,
        ),
      );
    }
  }

  /* --- b) cross-table: summary figure vs detail baseline − post -------- */

  // Reconcile the energy quantity column, identified by its unit.
  const summaryEnergyCol = findDataColumn(summary, /kwh/i);
  const summaryCostCol = findDataColumn(summary, /\(\$\/yr\)|\$\s*\/\s*yr/i);

  for (const match of selection.ecmMatches) {
    if (!match.detailTableId) {
      notChecked.push({
        title: `${match.ecmLabel}: cross-table check`,
        reason: 'No detail table was matched to this row, so its figure was not reconciled.',
      });
      continue;
    }

    const detailTable = byId.get(match.detailTableId);
    if (!detailTable) {
      notChecked.push({
        title: `${match.ecmLabel}: cross-table check`,
        reason: 'The matched detail table is no longer present in this document.',
      });
      continue;
    }

    const detail = readTable(detailTable);
    const detailEnergyCol = findDataColumn(detail, /kwh/i);

    if (summaryEnergyCol < 0 || detailEnergyCol < 0) {
      notChecked.push({
        title: `${match.ecmLabel}: cross-table check`,
        reason:
          'No column labelled in kWh could be identified in both tables, so the energy figures were not compared.',
      });
      continue;
    }

    const baselineRow = detail.dataRows.find((r) => /baseline/i.test(r.label));
    const savingsRow = detail.dataRows.find((r) => /savings/i.test(r.label));
    let postRow = detail.dataRows.find(
      (r) => r !== baselineRow && /post|installation/i.test(r.label),
    );
    let postByPosition = false;

    // PDF extraction frequently mangles the post-installation label — in the
    // DOE example it survives as the single word "use". When Baseline and
    // Savings are found, the row between them is the post-installation row.
    if (!postRow && baselineRow && savingsRow) {
      postRow = detail.dataRows.find(
        (r) => r.index > baselineRow.index && r.index < savingsRow.index,
      );
      postByPosition = Boolean(postRow);
    }

    if (!baselineRow || !postRow) {
      notChecked.push({
        title: `${match.ecmLabel}: cross-table check`,
        reason: `${detailTable.label} does not expose both a baseline and a post-installation row, so the difference could not be computed.`,
      });
      continue;
    }

    const summaryValue = valueAt(
      summary,
      selection,
      match.summaryRow,
      summaryEnergyCol,
      match.ecmLabel,
    );
    const baseline = valueAt(detail, selection, baselineRow.index, detailEnergyCol, baselineRow.label || 'Baseline use');
    const post = valueAt(
      detail,
      selection,
      postRow.index,
      detailEnergyCol,
      postRow.label || 'Post-installation use',
    );

    if (!summaryValue || !baseline || !post) {
      notChecked.push({
        title: `${match.ecmLabel}: cross-table check`,
        reason: 'One of the three figures could not be read, so nothing was computed.',
      });
      continue;
    }

    const difference = baseline.value - post.value;
    const delta = summaryValue.value - difference;

    const inputs = [
      {
        label: summaryValue.ref + (summaryValue.userCorrected ? ' — corrected by reviewer' : ''),
        value: summaryValue.raw,
      },
      {
        label: baseline.ref + (baseline.userCorrected ? ' — corrected by reviewer' : ''),
        value: baseline.raw,
      },
      {
        label:
          post.ref +
          (post.userCorrected ? ' — corrected by reviewer' : '') +
          (postByPosition
            ? ' — identified by position between Baseline and Savings, its printed label did not survive text extraction'
            : ''),
        value: post.raw,
      },
    ];

    const statedSavings = savingsRow
      ? valueAt(detail, selection, savingsRow.index, detailEnergyCol, savingsRow.label)
      : null;
    if (statedSavings) {
      inputs.push({
        label: statedSavings.ref + (statedSavings.userCorrected ? ' — corrected by reviewer' : ''),
        value: statedSavings.raw,
      });
    }

    const computation = `${fmt(baseline.value)} - ${fmt(post.value)} = ${fmt(difference)}\nsummary states ${fmt(summaryValue.value)}\ndifference = ${fmt(summaryValue.value)} - ${fmt(difference)} = ${fmt(delta)}`;

    if (delta === 0) {
      clean.push({
        title: `${match.ecmLabel}: summary agrees with ${detailTable.label}`,
        detail: `${fmt(baseline.value)} − ${fmt(post.value)} = ${fmt(difference)}, which is what the summary states.`,
        receipt: {
          kind: 'arithmetic',
          inputs,
          computation,
          result: `The summary figure equals baseline minus post-installation exactly.`,
        },
      });
      continue;
    }

    mark(summary.table.id, match.summaryRow, summaryEnergyCol);
    mark(detailTable.id, baselineRow.index, detailEnergyCol);
    mark(detailTable.id, postRow.index, detailEnergyCol);

    const pct = difference !== 0 ? Math.abs(delta / difference) * 100 : Infinity;
    const disagreement = makeFlag(
      doc,
      `flag-${CHECK_ID}-ecm${match.summaryRow}`,
      `${summary.table.label} and ${detailTable.label} report different savings for ${match.ecmLabel}`,
      `${match.ecmLabel}: the summary and detail tables disagree`,
      `${summary.table.label} / ${detailTable.label}`,
      summary.table.page,
      {
        kind: 'arithmetic',
        inputs,
        computation,
        result: `${detailTable.label} implies a saving of ${fmt(difference)}, while ${summary.table.label} states ${fmt(summaryValue.value)}. The two disagree by ${fmt(delta)}${Number.isFinite(pct) ? `, about ${pct.toFixed(0)}% of the detail figure` : ''}.${statedSavings && statedSavings.value === difference ? ` ${detailTable.label}'s own savings row states ${fmt(statedSavings.value)}, agreeing with its baseline arithmetic and not with the summary.` : ''}`,
      },
      summaryValue.raw,
    );
    flags.push(disagreement);

    /* --- c) rate consistency: which quantity does the cost support? ---- */
    const rateTable = selection.rateTableId ? byId.get(selection.rateTableId) : undefined;
    const rate = rateTable ? findUnitRate(rateTable) : null;
    const detailCostCol = findDataColumn(detail, /\(\$\/yr\)|\$\s*\/\s*yr/i);

    if (!rate || !rateTable) {
      // The disagreement stands on its own arithmetic. What is missing is the
      // one further answer that would say which of the two figures the plan's
      // own cost supports, so the finding carries the question rather than the
      // workspace inferring it from the shape of the receipt.
      disagreement.pendingAnswer = {
        question: 'rate-table',
        prompt:
          'The plan’s own energy rate decides which of these two figures the stated cost supports.',
      };
      notChecked.push({
        title: `${match.ecmLabel}: rate consistency`,
        reason: 'No rate table was confirmed, so the cost figure was not used to resolve the disagreement.',
      });
      continue;
    }

    const cost =
      (savingsRow && detailCostCol >= 0
        ? valueAt(detail, selection, savingsRow.index, detailCostCol, savingsRow.label)
        : null) ??
      (summaryCostCol >= 0
        ? valueAt(summary, selection, match.summaryRow, summaryCostCol, match.ecmLabel)
        : null);

    if (!cost) {
      notChecked.push({
        title: `${match.ecmLabel}: rate consistency`,
        reason: 'No annual cost figure could be read for this measure.',
      });
      continue;
    }

    const impliedQuantity = cost.value / rate.value;
    const candidates = [
      { name: `${summary.table.label} summary figure`, value: summaryValue.value },
      { name: `${detailTable.label} baseline minus post-installation`, value: difference },
    ];
    const scored = candidates.map((c) => ({
      ...c,
      product: c.value * rate.value,
      error: c.value === 0 ? Infinity : Math.abs(c.value * rate.value - cost.value) / cost.value,
    }));
    const supported = scored.filter((s) => s.error <= MATCHES_WITHIN);

    const rateRef = `${rateTable.label}, page ${rateTable.page}, row ${rate.row}, column ${rate.col}`;
    const rateInputs = [
      { label: `Rate — ${rateRef}`, value: rate.raw },
      {
        label: cost.ref + (cost.userCorrected ? ' — corrected by reviewer' : ''),
        value: cost.raw,
      },
      ...scored.map((s) => ({
        label: `Candidate quantity — ${s.name}`,
        value: fmt(s.value),
      })),
    ];

    const rateComputation = [
      ...scored.map(
        (s) =>
          `${fmt(s.value)} x ${rate.raw} = ${fmt(s.product)}   ${
            s.error <= MATCHES_WITHIN ? 'matches' : 'does not match'
          } the stated ${cost.raw}`,
      ),
      `${cost.raw} / ${rate.raw} = ${fmt(impliedQuantity)}`,
    ].join('\n');

    const resolution =
      supported.length === 1
        ? `At ${rate.raw} the stated cost of ${cost.raw} corresponds to ${fmt(impliedQuantity)}, which is the ${supported[0].name} (${fmt(supported[0].value)}) and not the other candidate. The cost figure therefore supports ${fmt(supported[0].value)}.`
        : supported.length === 0
          ? `At ${rate.raw} the stated cost of ${cost.raw} corresponds to ${fmt(impliedQuantity)}, which matches neither candidate within 1%. The cost figure does not resolve the disagreement.`
          : `At ${rate.raw} the stated cost of ${cost.raw} is within 1% of both candidates, so it does not distinguish between them.`;

    if (supported.length === 1) {
      flags.push(
        makeFlag(
          doc,
          `flag-${CHECK_ID}-rate${match.summaryRow}`,
          `The plan’s own cost figure shows which savings figure for ${match.ecmLabel} the money follows`,
          `${match.ecmLabel}: the cost figure identifies which quantity is real`,
          `${summary.table.label} / ${detailTable.label} / ${rateTable.label}`,
          detailTable.page,
          {
            kind: 'arithmetic',
            inputs: rateInputs,
            computation: rateComputation,
            result: resolution,
          },
          cost.raw,
        ),
      );
    } else {
      notChecked.push({
        title: `${match.ecmLabel}: rate consistency`,
        reason: resolution,
      });
    }
  }

  /* --- cross-overview: a second project table restating the totals ---- */
  const overviewTable = selection.overviewTableId
    ? byId.get(selection.overviewTableId)
    : undefined;

  if (overviewTable && totalRow) {
    const overview = readTable(overviewTable);

    // Compare any project total whose column unit appears in both tables.
    const units = ['MBtu', 'kWh', 'kW-yr'];
    for (const unit of units) {
      const re = new RegExp(unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
      const sCol = findDataColumn(summary, re);
      const oCol = findDataColumn(overview, re);
      if (sCol < 0 || oCol < 0) continue;

      const sValue = valueAt(summary, selection, totalRow.index, sCol, totalRow.label);
      // The overview's savings row is its first data row.
      const oRow = overview.dataRows[0];
      const oValue = oRow ? valueAt(overview, selection, oRow.index, oCol, oRow.label) : null;
      if (!sValue || !oValue) continue;

      const delta = sValue.value - oValue.value;
      const inputs = [
        { label: sValue.ref, value: sValue.raw },
        { label: oValue.ref, value: oValue.raw },
      ];
      const computation = `${fmt(sValue.value)} - ${fmt(oValue.value)} = ${fmt(delta)}`;

      if (delta === 0) {
        clean.push({
          title: `${summary.table.label} and ${overviewTable.label} agree on project ${unit}`,
          detail: `Both state ${sValue.raw}.`,
          receipt: {
            kind: 'arithmetic',
            inputs,
            computation,
            result: `Both tables state ${fmt(sValue.value)} ${unit}.`,
          },
        });
      } else {
        mark(summary.table.id, totalRow.index, sCol);
        if (oRow) mark(overviewTable.id, oRow.index, oCol);
        flags.push(
          makeFlag(
            doc,
            `flag-${CHECK_ID}-overview-${unit}`,
            `${summary.table.label} and ${overviewTable.label} state different project totals in ${unit}`,
            `${summary.table.label} and ${overviewTable.label} state different project totals`,
            `${summary.table.label} / ${overviewTable.label}`,
            overviewTable.page,
            {
              kind: 'arithmetic',
              inputs,
              computation,
              result: `${summary.table.label} states ${fmt(sValue.value)} ${unit} for the project and ${overviewTable.label} states ${fmt(oValue.value)} ${unit} — a difference of ${fmt(delta)}. Both describe the same project.`,
            },
            sValue.raw,
          ),
        );
      }
    }
  }

  return { flags, highlights, clean, notChecked };
}

/* ==========================================================================
   Pre-selection helpers for the confirmation UI
   ========================================================================== */

/** Best detail table for an ECM row, by name similarity. Never applied silently. */
export function bestDetailFor(
  ecmLabel: string,
  details: Candidate[],
): string | null {
  const needle = ecmLabel.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').trim();
  if (!needle) return null;

  const words = needle.split(/\s+/).filter((w) => w.length > 2);
  let best: { id: string; score: number } | null = null;

  for (const candidate of details) {
    const hay = `${candidate.caption} ${candidate.label}`.toLowerCase();
    let score = 0;
    if (hay.includes(needle)) score += 5;
    for (const word of words) if (hay.includes(word)) score += 2;
    if (score > 0 && (!best || score > best.score)) {
      best = { id: candidate.tableId, score };
    }
  }

  return best ? best.id : null;
}

/** The ECM rows a reviewer will be asked to match. */
export function summaryEcmRows(
  table: ExtractedTable,
): { index: number; label: string }[] {
  const shape = readTable(table);
  return shape.dataRows
    .filter((r) => looksLikeEcmLabel(r.label))
    .map((r) => ({ index: r.index, label: r.label }));
}
